import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { X } from 'lucide-react';
import { memo, useState } from 'react';

const DialogStyled = styled(Dialog)(() => {
    return {
        '& .MuiDialog-paper': {
            borderRadius: '14px',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
        },
        '& .MuiDialogContent-root': {
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            '& .input-section': {
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                '& .MuiTypography-root': {
                    color: '#6B7985',
                    fontSize: '15px',
                    lineHeight: '20px',
                },
                '& .MuiButton-root': {
                    padding: '8px 16px',
                    borderRadius: '8px',
                    borderColor: '#E4E6EB',
                    backgroundColor: '#FFFFFF',
                    color: '#3A4B59',
                    minHeight: '40px',
                    maxHeight: '40px',
                },
                '& .selected': {
                    backgroundColor: '#6A76FB',
                    color: 'white',
                },
            },
        },
    };
});

const MemoizedExportProspectsModal = ({
    onClose,
    onSubmit,
    numberSelectedProspects,
    numberAllProspects,
    open,
}: {
    onClose: () => void;
    onSubmit: (format: 'csv' | 'xlsx', selectedProspects: 'selected' | 'all') => void;
    numberSelectedProspects: number;
    numberAllProspects: number;
    open: boolean;
}) => {
    const [selectedFormat, setSelectedFormat] = useState<'csv' | 'xlsx'>('csv');
    const [selectedProspects, setSelectedProspects] = useState<'selected' | 'all'>('selected');

    const handleSubmit = () => {
        onSubmit(selectedFormat, selectedProspects);
        onClose();
    };

    return (
        <DialogStyled fullWidth open={open} onClose={onClose}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px',
                }}
            >
                <DialogTitle
                    sx={{
                        padding: '24px 24px 0',
                    }}
                >
                    <Box>
                        <Typography
                            sx={{
                                color: '#23323F',
                                fontSize: '19px',
                                fontWeight: 500,
                                lineHeight: '28px',
                            }}
                        >
                            Export Prospects
                        </Typography>
                        <Typography
                            sx={{
                                color: '#6B7985',
                                fontSize: '15px',
                                lineHeight: '20px',
                            }}
                        >
                            Download your Prospects in CSV or Excel format
                        </Typography>
                    </Box>
                    <IconButton sx={{ position: 'absolute', top: '24px', right: '16px' }} onClick={onClose}>
                        <X size={20} />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box className="input-section">
                        <Typography>Choose which Prospects to export</Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: '16px',
                            }}
                        >
                            <Button
                                variant="outlined"
                                disableElevation
                                className={selectedProspects === 'selected' ? 'selected' : ''}
                                onClick={() => setSelectedProspects('selected')}
                            >
                                {`Selected Prospects (${numberSelectedProspects})`}
                            </Button>
                            <Button
                                variant="outlined"
                                disableElevation
                                className={selectedProspects === 'all' ? 'selected' : ''}
                                onClick={() => setSelectedProspects('all')}
                            >
                                {`All Prospects (${numberAllProspects})`}
                            </Button>
                        </Box>
                    </Box>
                    <Box className="input-section">
                        <Typography>Choose the format you want to download your Prospects in</Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: '16px',
                            }}
                        >
                            <Button
                                variant="outlined"
                                disableElevation
                                className={selectedFormat === 'csv' ? 'selected' : ''}
                                onClick={() => setSelectedFormat('csv')}
                            >
                                CSV
                            </Button>
                            <Button
                                variant="outlined"
                                disableElevation
                                className={selectedFormat === 'xlsx' ? 'selected' : ''}
                                onClick={() => setSelectedFormat('xlsx')}
                            >
                                Excel
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions
                    sx={{
                        padding: '0px 24px 24px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '16px',
                        }}
                    >
                        <Button
                            variant="outlined"
                            disableElevation
                            sx={{
                                padding: '8px 16px',
                                borderRadius: '8px',
                                borderColor: '#E4E6EB',
                                backgroundColor: '#FFFFFF',
                                color: '#3A4B59',
                                minHeight: '40px',
                                maxHeight: '40px',
                            }}
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            disableElevation
                            sx={{
                                padding: '8px 16px',
                                borderRadius: '8px',
                                backgroundColor: '#6A76FB',
                                minHeight: '40px',
                                maxHeight: '40px',
                                minWidth: '123px',
                                transition: 'background-color 0.3s, transform 0.3s',
                                ':hover': {
                                    backgroundColor: '#838dfc',
                                },
                            }}
                            onClick={handleSubmit}
                        >
                            <Typography
                                component={'span'}
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    lineHeight: '21px',
                                    color: 'white',
                                    transition: 'color 0.3s',
                                }}
                            >
                                Export
                            </Typography>
                        </Button>
                    </Box>
                </DialogActions>
            </Box>
        </DialogStyled>
    );
};

export const ExportProspectsModal = memo(MemoizedExportProspectsModal);
